import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import { dateFormat, featuresCodes } from "../../../../constants/Constants";
import PolicyCardStyles from "./PolicyCardStyles";
import {
  IDownloadIdCardParams,
  IPolicy,
} from "../../../../models/user/dashboard/Dashboard";
import { FC, useMemo, useState, useRef } from "react";
import { currencyFormat, isFeatureAvailable } from "../../../../utils/Utility";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { setClaimsData } from "../../DashboardSlice";
import { setPolicyDetailsHeader } from "../../../../store/user/policyDetails/policyDetailsSlice";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import { GetDownloadIdCard } from "../../DashboardAction";
import { RootState } from "../../../../store/store";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileAClaim from "../../../../components/FileAClaim/FileAClaim";
import { useTranslation } from "react-i18next";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";
import PolicyChange from "../PolicyChange/PolicyChange";
import { resetCommonSlice } from "../../../../store/common/CommonSlice";

interface props {
  policy: IPolicy;
}

const PolicyCard: FC<props> = ({ policy }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { features } = useAppSelector((state: RootState) => state.features);

  const isMobile = useMediaQuery("(max-width:768px)");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fielAClaimOpen, setFileAClaimOpen] = useState<boolean>(false);
  const [changePolicyOpen, setChangePolicyOpen] = useState<boolean>(false);
  const [policyChangeDrawerTitle, setPolicyChangeDrawerTitle] = useState("");
  const resetPolicyChangeRef = useRef<(() => void) | null>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const redirectToDetails = () => {
    dispatch(setClaimsData(null));
    dispatch(setPolicyDetailsHeader(null));
    navigate(
      `/customer/policyDetails/${policy.policyId}/${policy.carrier}/${policy.system}/${policy.lobCode}`
    );
  };

  const handleDownloadIdCard = (policyId: string, lobCode: string) => {
    const requestParams: IDownloadIdCardParams = {
      policyId,
      lobCode,
    };
    dispatch(GetDownloadIdCard(requestParams));
  };

  const diffInDays = useMemo(() => {
    const dueDate = moment(policy.dueDate).startOf("day");
    const currentDate = moment().startOf("day");
    return dueDate.diff(currentDate, "days");
  }, [policy]);

  const getRiskText = (policy: IPolicy) => {
    switch (policy.lobCode) {
      case "PA":
        return policy.riskCount > 1 ? t("Vehicles") : t("Vehicle");
      case "HO":
        return (
          policy.address.addr1 +
          " " +
          policy.address.city +
          " " +
          policy.address.stateProvCd +
          " " +
          policy.address.regionISOCd +
          " " +
          policy.address.postalCode
        );
      case "PU":
        return currencyFormat("us", +policy.personalUmbrellaLiabilityLimit);
      case "BOP":
        return policy.insuredList?.map((insured) =>
          insured.dBAName ? insured.dBAName : insured.businessName
        );
      case "FO":
        return policy.locationCount > 1 ? t("Locations") : t("Location");
    }
  };

  const getDueDateText = () => {
    if (diffInDays === 0) {
      return (
        <Typography sx={PolicyCardStyles.dueDate}>
          {t("Due")}: <span style={{ fontWeight: "bold" }}>Today</span>
        </Typography>
      );
    } else if (diffInDays > 0) {
      return (
        <Typography sx={PolicyCardStyles.dueDate}>
          {t("Due")}: {`${moment(policy.dueDate).format(dateFormat)}`} in{" "}
          <span style={{ fontWeight: "bold" }}>
            {diffInDays} {diffInDays === 1 ? t("day") : t("days")}
          </span>
        </Typography>
      );
    } else {
      return (
        <Typography sx={PolicyCardStyles.dueDate}>
          {t("Due")}: {`${moment(policy.dueDate).format(dateFormat)}`}{" "}
          {t("Overdue")} by{" "}
          <span style={{ fontWeight: "bold" }}>
            {Math.abs(diffInDays)} {Math.abs(diffInDays) === 1 ? "day" : "days"}
          </span>
        </Typography>
      );
    }
  };

  const getIcon = (linecode: string) => {
    switch (linecode) {
      case "PA":
        return <CustomIcon iconName="Car" />;
      case "HO":
        return <CustomIcon iconName="Home" />;
      case "PU":
        return <CustomIcon iconName="Umbrella" />;
      case "BOP":
        return <CustomIcon iconName="Business" width="60px" height="60px" />;
      case "FO":
        return <CustomIcon iconName="Farm" />;
    }
  };

  const handlePolicyChangeOpenClick = () => {
    setPolicyChangeDrawerTitle("");
    setChangePolicyOpen(true);
  };

  const getFeatureCodes = (featureCode: string) => {
    if (!featureCode || !policy.lobCode) {
      return;
    }
    const name: any =
      featuresCodes[featureCode as keyof typeof getFeatureCodes];
    return name && name[policy.lobCode];
  };

  const closePolicyChangeDrawer = () => {
    dispatch(resetCommonSlice());
    setChangePolicyOpen(false);
    setPolicyChangeDrawerTitle("");
  };

  const handleBackToPolicyChange = () => {
    setPolicyChangeDrawerTitle("");
    if (resetPolicyChangeRef.current) {
      resetPolicyChangeRef.current();
    }
  };

  return (
    <>
      <Card sx={PolicyCardStyles.policyCard}>
        <Box sx={PolicyCardStyles.containerOne}>
          <Box sx={PolicyCardStyles.policyNumber}>
            {getIcon(policy.lobCode)}
            <Box>
              <Typography
                id="policycard_policy-number"
                data-test-id="test-policycard_policy-number"
                aria-label="Policy Number"
                sx={PolicyCardStyles.cardTopPolicyHeading}
              >
                {t("Policy Number")}
              </Typography>
              <Typography
                id="policycard_policy-number-value"
                data-test-id="test-policycard_policy-number-value"
                aria-label="Policy Number Value"
                sx={PolicyCardStyles.cardTopPolicyNumber}
              >
                {policy.carrierPolicyNo}
              </Typography>
            </Box>
          </Box>
          <Box sx={PolicyCardStyles.policyStatusContainer}>
            {policy.carrierPolicyStatus ? (
              <>
                <Typography
                  id="policycard_status"
                  data-test-id="test-policycard_status"
                  aria-label="Policy Status"
                  sx={PolicyCardStyles.policyStatusHeading}
                >
                  {t("Status")}
                </Typography>
                <Typography
                  id="policycard_status-value"
                  data-test-id="test-policycard_status-value"
                  aria-label="Policy Status Value"
                  sx={PolicyCardStyles.policyStatus}
                >
                  {policy.carrierPolicyStatus !== "Cancelled" &&
                  policy?.carrierPolicyStatus !== "Cancel Notice"
                    ? ` ${t("Expires on")} ${moment(policy.expiryDate).format(
                        dateFormat
                      )}`
                    : policy.carrierPolicyStatus
                    ? policy.carrierPolicyStatus
                    : ""}
                </Typography>
              </>
            ) : (
              <></>
            )}
          </Box>
          <Typography
            id="policycard_insured-name"
            data-test-id="test-policycard_insured-name"
            aria-label="Insured Name"
            sx={PolicyCardStyles.productName}
          >
            {policy.lobCode === "BOP"
              ? policy.insuredList?.map((insured) => insured.businessName)
              : `${policy.insuredName}${
                  policy.insuredNameJoint ? ` | ${policy.insuredNameJoint}` : ""
                }`}
          </Typography>
        </Box>
        <Box sx={PolicyCardStyles.premium}>
          <Typography
            id="policycard_premium"
            data-test-id="test-policycard_premium"
            aria-label="Policy Premium"
            sx={PolicyCardStyles.cardBodyPremium}
          >
            {t("Premium")}:{" "}
            <span style={{ fontWeight: "bold" }}>
              {policy.finalPremiumAmount > 0
                ? currencyFormat("us", policy.finalPremiumAmount)
                : "$" + 0}
            </span>{" "}
            | {t("Term")}:{" "}
            <span style={{ fontWeight: "bold" }}>{policy.tenure}</span>
          </Typography>
        </Box>
        {policy.dueDate ? (
          getDueDateText()
        ) : (
          <Typography
            id="policycard_no-pending-invoices"
            data-test-id="test-policycard_no-pending-invoices"
            aria-label="No Pending Invoices"
            sx={PolicyCardStyles.dueDate}
          >
            {t("Due")}: {t("No Pending Invoices")}
          </Typography>
        )}
        <Box
          id="policycard_risk-info"
          data-test-id="test-policycard_risk-info"
          aria-label="Risk Information"
          sx={{
            display: "inline-flex",
            justifyContent: "space-between",
            gap: isMobile ? "10px" : "0px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Typography
            id="policycard_risk-count"
            data-test-id="test-policycard_risk-count"
            aria-label="Risk Count"
            sx={PolicyCardStyles.dueDate}
          >
            <span style={PolicyCardStyles.vehicleCount}>
              {policy.lobCode === "PA"
                ? policy.riskCount
                : policy.lobCode === "FO"
                ? policy.locationCount
                : ""}{" "}
              {getRiskText(policy)}
            </span>
          </Typography>
          {policy.payPlanType === "Automatic" ? (
            <Box
              id="policycard_auto-debit"
              data-test-id="test-policycard_auto-debit"
              aria-label="Auto Debit Status"
              sx={PolicyCardStyles.autoDebitActive}
            >
              <CheckCircleOutlineIcon sx={PolicyCardStyles.checkCircleIcon} />
              <Typography sx={PolicyCardStyles.cardBodyPremiumAuto}>
                {t("Auto Debit Active")}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
        </Box>
        <Box
          id="policycard_actions"
          data-test-id="test-policycard_actions"
          aria-label="Policy Actions"
          sx={PolicyCardStyles.actions}
        >
          <Box sx={{ display: "flex", gap: "10px" }}>
            {isFeatureAvailable({
              featureCodes: getFeatureCodes("View Policy"),
              features,
            }) ? (
              <Button
                id="policycard_view-policy-button"
                data-test-id="test-policycard_view-policy-button"
                aria-label="View Policy Details"
                variant="contained"
                onClick={redirectToDetails}
                // disabled={policy.lobCode === "HO"}
              >
                {t("View Policy")}
              </Button>
            ) : (
              <></>
            )}
            {isFeatureAvailable({
              featureCodes: getFeatureCodes("File A Claim"),
              features,
            }) ? (
              <Button
                id="policycard_file-claim-button"
                data-test-id="test-policycard_file-claim-button"
                aria-label="File A Claim"
                variant="contained"
                onClick={() => {
                  setFileAClaimOpen(true);
                }}
              >
                {t("File A Claim")}
              </Button>
            ) : (
              <></>
            )}
            {isFeatureAvailable({
              featureCodes: getFeatureCodes("Change Policy Single Screen"),
              features,
            }) ? (
              <Button
                id="policycard_change-policy-button"
                data-test-id="test-policycard_change-policy-button"
                aria-label="Change Policy"
                variant="contained"
                onClick={handlePolicyChangeOpenClick}
              >
                {t("Change Policy")}
              </Button>
            ) : (
              <></>
            )}
          </Box>

          {/* {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_insurance_id"],
            features,
          }) && policy.lobCode === "PA" ? (
            <IconButton
              id="policycard_more-options-button"
              data-test-id="test-policycard_more-options-button"
              aria-label="More Options"
              onClick={handleClick}
              sx={{
                borderRadius: "34px",
                border: "1px solid #8E8E8E",
              }}
            >
              <MoreVertIcon
                sx={{
                  color: theme.palette.secondary.main,
                }}
              />
            </IconButton>
          ) : (
            <></>
          )}
          <Menu
            id="policycard_more-options-menu"
            data-test-id="test-policycard_more-options-menu"
            aria-label="More Options Menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "policycard_more-options-button",
            }}
          >
            <MenuItem
              id="policycard_download-id-option"
              data-test-id="test-policycard_download-id-option"
              aria-label="Download ID Card"
              onClick={() =>
                handleDownloadIdCard(policy.policyId, policy?.lobCode)
              }
            >
              {t("Download ID")}
            </MenuItem>
          </Menu> */}
        </Box>
      </Card>
      {fielAClaimOpen ? (
        <FileAClaim
          open={fielAClaimOpen}
          setOpen={setFileAClaimOpen}
          policyId={policy.policyId}
          lobCode={policy.lobCode}
          carrier={policy.carrier || ""}
          system={policy.system || ""}
        />
      ) : (
        <></>
      )}
      <CustomDrawer
        open={changePolicyOpen}
        onClose={closePolicyChangeDrawer}
        title={
          policyChangeDrawerTitle
            ? t(policyChangeDrawerTitle)
            : `${t("Change Policy")} ${policy.carrierPolicyNo}`
        }
        content={
          <PolicyChange
            setPolicyChangeDrawerTitle={setPolicyChangeDrawerTitle}
            lobCode={policy?.lobCode}
            policyId={policy?.policyId}
            closePolicyChangeDrawer={closePolicyChangeDrawer}
            setChangePolicyOpen={setChangePolicyOpen}
            resetPolicyChangeState={resetPolicyChangeRef}
          />
        }
        onBack={policyChangeDrawerTitle ? handleBackToPolicyChange : undefined}
        // headerStyles={{
        //   "& .MuiTypography-root": {
        //     fontFamily: "Noto Sans !important",
        //     fontWeight: "bold",
        //   },
        // }}
        contentStyles={{
          padding:
            policyChangeDrawerTitle && policyChangeDrawerTitle?.length
              ? 0
              : "1.25rem 1.5rem",
        }}
      />
    </>
  );
};

export default PolicyCard;
